import styles from 'typescript/apps/checkout_v3/components/recaptcha/recaptcha_modal.module.scss';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MarketDialog, MarketDivider } from '@market/react';
import { captureException } from '@sentry/react';
import {
  FieldValues,
  UseFormHandleSubmit,
  useFormContext,
} from 'react-hook-form';
import { trackCdp } from 'utilities/tracking';
import { ReactComponent as RecaptchaHeaderImage } from 'images/modal_headers/recaptcha_header.svg';
import RecaptchaWorkaround from 'typescript/apps/checkout_v3/components/recaptcha/recaptcha_workaround';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import {
  useCompleteOrder,
  useGetOrderSummary,
  useRequestRecaptchaFallback,
} from 'typescript/hooks/use_order_actions_client';
import {
  CHECKOUT_ROUTES,
  CompleteOrderError,
} from 'typescript/constants/enums/checkout';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';
import { COMPONENT_NAMES, FEATURES } from 'typescript/constants/tracking';
import CDP_EVENT_NAMES from 'typescript/constants/cdp_events';
import { useLogCompleteOrderAction } from 'typescript/apps/checkout_v3/hooks/use_log_complete_order_action';

interface RecaptchaModalProps {
  handleFormSubmit: UseFormHandleSubmit<FieldValues, undefined>;
}

const RecaptchaModal = ({ handleFormSubmit }: RecaptchaModalProps) => {
  const { t } = useTranslation();
  const { languageCode, countryCode } = useLocaleInfo();
  const { reset: resetForm, getValues: getFormValues } = useFormContext();
  const { recaptchaKey, orderNumber, trackingData } = useCheckoutFlow();
  const { completeOrder } = useCompleteOrder({ orderNumber });
  const { requestRecaptchaFallback } = useRequestRecaptchaFallback({
    orderNumber,
    countryCode,
    languageCode,
  });

  const {
    showRecaptcha,
    setShowRecaptcha,
    isCompletingOrder,
    setCompleteOrderError,
  } = useCompleteOrderContext();

  const history = useHistory();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });

  const { logCompleteOrderAction } = useLogCompleteOrderAction({
    orderNumber,
    orderSummaryData,
  });

  // Reset the form's touched/dirty state when dismissing the recaptcha modal
  // so the delivery page CTA re-enables. The forced blur in handleSubmitWithLogging
  // marks a field as touched, which disables the CTA via the canContinue check.
  const dismissRecaptcha = () => {
    resetForm(getFormValues());
    setShowRecaptcha(false);
  };

  const onCompleteOrder = (recaptchaResponse: string) => (_event) => {
    if (isCompletingOrder) {
      return;
    }

    completeOrder(
      { recaptchaResponse },
      {
        onSuccess: () => {
          logCompleteOrderAction({ status: 'success' });
          setShowRecaptcha(false);
        },
        onError: (error) => {
          trackCdp({
            name: CDP_EVENT_NAMES.CHECKOUT_V3.DELIVERY
              .COMPLETING_ORDER_ERROR_DELIVERY,
            params: {
              feature: FEATURES.SHOP.NAME,
              step: COMPONENT_NAMES.APPS.CHECKOUT_V3.DELIVERY,
              error: error?.message,
              ...trackingData,
            },
          });

          logCompleteOrderAction({
            status: 'error',
            errorDetails: error?.message,
          });
          setCompleteOrderError(CompleteOrderError.GENERIC);
          setShowRecaptcha(false);
        },
        onSettled: (_, error) => {
          // if there is no error, we should redirect to the complete page
          if (!error) {
            history.push(CHECKOUT_ROUTES.COMPLETE);
          }
        },
      },
    );
  };

  const handleSubmit = (recaptchaResponse: string) => {
    handleFormSubmit(onCompleteOrder(recaptchaResponse))();
  };

  if (!showRecaptcha) {
    return null;
  }

  return (
    <MarketDialog className={styles.dialog} isLoading={false} persistent={true}>
      <div className="shop-grid-container">
        <div className="shop-grid-item-full">
          <RecaptchaHeaderImage />
        </div>
        <h2 className="shop-grid-item-full">
          {t('checkout.dialog.recaptcha.title')}
        </h2>
        <p className="shop-grid-item-full">
          {t('checkout.dialog.recaptcha.body')}
        </p>
        <RecaptchaWorkaround
          className={`${styles.recaptchaContainer} shop-grid-item-full`}
          languageCode={languageCode}
          siteKey={recaptchaKey}
          onVerify={handleSubmit}
          onError={async (recaptchaError?: Error) => {
            captureException(recaptchaError, {
              tags: {
                orderNumber,
              },
            });

            try {
              // try to get a server-generated challenge token
              const fallbackToken = await requestRecaptchaFallback();
              handleSubmit(fallbackToken);
            } catch (fallbackError) {
              // if fallback challenge fails, capture error and close modal
              captureException(fallbackError, {
                tags: {
                  orderNumber,
                },
              });

              await logCompleteOrderAction({
                status: 'cancelled',
                errorDetails: 'recaptcha_fallback_failure',
              });

              // Show error and close the recaptcha modal
              setCompleteOrderError(CompleteOrderError.GENERIC);
              dismissRecaptcha();
            }
          }}
        />
        <MarketDivider
          className={`${styles.removeMargin} shop-grid-item-full`}
          size="thin"
        />
        <p className="shop-grid-item-full">
          {`${t('checkout.dialog.notReady')} `}
          <a
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              // need to await this to ensure the action is logged before dismissing the modal
              // otherwise the action will not be logged
              await logCompleteOrderAction({ status: 'cancelled' });
              dismissRecaptcha();
            }}
          >
            {t('checkout.dialog.cancelCTA')}
          </a>
        </p>
      </div>
    </MarketDialog>
  );
};

export default RecaptchaModal;
